import bookingService from "@services/booking/bookingService"
import fileService from "@services/common/fileService"
import type { PagedResultDto } from "@services/dto/pagedResultDto"
import { action, makeAutoObservable, observable } from "mobx"

class BookingStore {
  @observable isLoading!: boolean
  @observable detailLoading!: boolean
  @observable tableData!: PagedResultDto<any>
  @observable myBookings!: PagedResultDto<any>
  @observable detailData!: any
  @observable overviewData!: any
  @observable auditLogData!: any
  @observable listBookingHistories!: any
  @observable listFloors!: any
  @observable listStackingPlan!: any
  @observable printReviewData!: any
  @observable listHistoryPrint!: PagedResultDto<any>
  constructor() {
    makeAutoObservable(this)
    this.tableData = { items: [], totalCount: 0 }
    this.myBookings = { items: [], totalCount: 0 }
    this.isLoading = false
    this.printReviewData = {}
    this.auditLogData = []
    this.listBookingHistories = []
    this.listFloors = []
    this.listStackingPlan = []
    this.overviewData = {} 
    this.listHistoryPrint = { items: [], totalCount: 0 }
  }

  @action
  async createOrUpdate(body: any, files?: any[]) {
    this.isLoading = true
    if (!body.id) {
      const result = await bookingService
        .create(body)
        .finally(() => (this.isLoading = false))
      this.detailData = result

      this.tableData.items.push(result)

      const { uniqueId } = result
      if (files && files.length && uniqueId) {
        const filesBody = {
          moduleName: "BOOKING_UNC",
          uniqueId: uniqueId,
          descriptions: [...files].map((item) => item?.description),
        }

        await fileService.uploadDocumentWithModuleName(
          [...files].map((item) => item?.file),
          filesBody
        )
      }
    } else {
      const result = await bookingService
        .update(body)
        .finally(() => (this.isLoading = false))
      this.tableData.items = this.tableData.items.map((x) => {
        if (x.id === body.id) x = result
        return x
      })

      const { uniqueId } = result
      if (files && files.length && uniqueId) {
        const filesBody = {
          moduleName: "BOOKING_UNC",
          uniqueId: uniqueId,
          descriptions: [...files].map((item) => item?.description),
        }

        await fileService.uploadDocumentWithModuleName(
          [...files].map((item) => item?.file),
          filesBody
        )
      }
    }
  }

  /** Xoá booking nháp (FB-005) — chỉ gọi cho booking chưa gửi duyệt. */
  @action
  async delete(id: number) {
    this.isLoading = true
    await bookingService.delete(id).finally(() => (this.isLoading = false))
    this.tableData.items = this.tableData.items.filter((x) => x.id !== id)
    this.tableData.totalCount = Math.max(this.tableData.totalCount - 1, 0)
  }

  @action
  async newBooking() {
    this.detailData = {
      id: 0,
    }
  }

  @action
  async activateOrDeactivate(id: number, isActive) {
    this.isLoading = true
    await bookingService
      .activateOrDeactivate(id, isActive)
      .finally(() => (this.isLoading = false))
  }
  @action
  async bulkActive(id: number[]) {
    await bookingService.bulkActive(id)
  }
  @action
  async get(id: number, isViewFull?: boolean) {
    this.detailLoading = true
    const result = await bookingService
      .get(id, isViewFull)
      .finally(() => (this.detailLoading = false))
    this.detailData = result
  }
  @action
  async getBookingIdByUniqueId(uniqueId: any) {
    const result = await bookingService.getBookingIdByUniqueId(uniqueId)

    return result
  }
  @action
  async getOverview() {
    const result = await bookingService.getOverview()
    this.overviewData = result
  }
  @action
  async emptyTable() {
    this.tableData = { items: [], totalCount: 0 }
  }
  @action
  async updateTableData(data) {
    this.tableData = data
  }

  @action
  async getAll(params: any) {
    this.isLoading = params?.loading ?? true
    const result = await bookingService
      .getAll(params)
      .finally(() => (this.isLoading = false))
    this.tableData = result
  }

  @action
  async getMyBookings(params: any) {
    this.isLoading = true
    const result = await bookingService
      .getMyBookings(params)
      .finally(() => (this.isLoading = false))
    this.myBookings = result
  }

  @action
  async getAuditLog(id: any) {
    const result = await bookingService.getAuditLog(id)

    this.auditLogData = result
  }
  @action
  async getListBookingHistories(id: any) {
    const result = await bookingService.getListBookingHistories(id)

    this.listBookingHistories = result
    return result
  }
  // Change unit

  @action
  async updateUnitForBooking(body: any) {
    this.detailLoading = true
    await bookingService
      .updateUnitForBooking(body)
      .finally(() => (this.detailLoading = false))
  }

  // floor map

  @action
  async getListBookingForInqueue(params: any) {
    this.isLoading = true
    const result = await bookingService
      .getListBookingForInqueue(params)
      .finally(() => (this.isLoading = false))

    this.listFloors = result?.floors
  }
  @action
  async getListStackingPlanByTower(params: any) {
    this.isLoading = true
    const result = await bookingService
      .getListStackingPlanByTower(params)
      .finally(() => (this.isLoading = false))

    this.listStackingPlan = result?.floors
  }
  @action
  async getUnitDetail(params: any) {
    this.detailLoading = true
    const result = await bookingService
      .getUnitDetail(params)
      .finally(() => (this.detailLoading = false))
    return result
  }

  @action
  async changePriorityBooking(body: any) {
    this.detailLoading = true
    await bookingService
      .changePriorityBooking(body)
      .finally(() => (this.detailLoading = false))
  }

  @action
  async startSync(body: any) {
    await bookingService.startSync(body)
  }

  @action
  async exportTemplateBooking(params: any) {
    this.isLoading = true
    await bookingService
      .exportTemplateBooking(params)
      .finally(() => (this.isLoading = false))
  }
  @action
  async validateBookingImport(file: File) {
    this.isLoading = true
    const result = await bookingService
      .validateBookingImport(file)
      .finally(() => (this.isLoading = false))
    return result
  }
  @action
  async importBooking(file: File) {
    this.isLoading = true
    await bookingService
      .importBooking(file)
      .finally(() => (this.isLoading = false))
  }
  @action
  async emptyPrintData() {
    this.printReviewData = ""
  }
  @action
  async generateContract(body: any) {
    const result = await bookingService.generateBooking(body)

    this.printReviewData = result
    return result
  }

  @action
  async exportTemplateBookingRepresentative(params: any) {
    this.isLoading = true
    await bookingService
      .exportTemplateBookingRepresentative(params)
      .finally(() => (this.isLoading = false))
  }

  @action
  async importTemplateBookingRepresentative(file: File) {
    this.isLoading = true
    await bookingService
      .importTemplateBookingRepresentative(file)
      .finally(() => (this.isLoading = false))
  }

  @action 
  async validateBookingImportRepresentative(file: File) {
    this.isLoading = true
    const result = await bookingService
      .validateBookingImportRepresentative(file)
      .finally(() => (this.isLoading = false))
    return result
  }

  @action
  async updateGenerateBooking(body: any) {
    this.isLoading = true
    await bookingService
      .updateGenerateBooking(body)
      .finally(() => (this.isLoading = false))
  }

  @action
  async getListHistoryPrint(params: any) {
    this.isLoading = true
    const result = await bookingService
      .getListHistoryPrint(params)
      .finally(() => (this.isLoading = false))
    this.listHistoryPrint = result
    return result
  }

  @action
  async generateUnitBookingDocument(body: any) {
    this.isLoading = true
    const result = await bookingService
      .generateUnitBookingDocument(body)
      .finally(() => (this.isLoading = false))
    this.printReviewData = result
    return result
  }

}

export default BookingStore
